import { FC, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Organization } from "../../../api/models/Organization";
import { User } from "../../../api/models/User";
import { requestsRepository } from "../../../api/RequestsRepository";
import { CustomButton } from "../../CustomButton/CustomButton";
import { CustomSelect } from "../../CustomSelect/CustomSelect";
import { Input } from "../../Input/Input";
import { Spin } from "../../Spin/Spin";
import { ModalComponent } from "../UiModals";
import { CancelIcon } from "../../icons/CacnelIcon";
import { RadioButton } from "../../Radiobutton/RadioButton";

interface IProps {
    onSuccess: (data: any) => void;
    userId: string;
    userName: string;
    description: string;
    sessionId: string;
    chats: any[]
}

interface IPropsActions extends IProps {
    close: () => void;
}

const Modal: FC<IPropsActions> = ({
                                      close,
                                      onSuccess,
                                      userId,
                                      userName,
                                      description,
                                      sessionId,
                                      chats
                                  }) => {
    const [textValue, setTextValue] = useState("");
    const [organization, setOrganization] = useState<any>(null);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [newUser, setNewUser] = useState<any>(null);
    const [users, setUsers] = useState<User[]>([]);
    const [organizationsSearch, setOrganizationsSearch] = useState<boolean>(false);
    const [usersSearch, setUsersSearch] = useState<boolean>(false);
    const [saveLoading, setSaveLoading] = useState<boolean>(false);

    useEffect(() => {
        setOrganizationsSearch(true);
        requestsRepository.ChatApiService.getOrganizations(sessionId).then((res) => {
            if (res.success) {
                setOrganizations(res.result);
            }
        }).finally(() => setOrganizationsSearch(false));
    }, [sessionId]);

    const organizationOptions = useMemo(() => {
        return organizations.map((x: any) => ({value: x.id, label: x.name}));
    }, [organizations]);

    const search = () => {
        setUsersSearch(true);
        setNewUser(null);
        requestsRepository.ChatApiService.searchUsers(sessionId, textValue, organization).then((res: any) => {
            if (!res.success) {
                toast.error(res.message);
            } else {
                setUsers(res.result);
            }
        }).finally(() => setUsersSearch(false));
    }
    
    const addNewContact = async () => {
        let roomId = null;
        try {
            const user = users.find(x => x.id === newUser);
            if (!user || (user.roomId && user.isVisible)) {
                return;
            }
            
            setSaveLoading(true);
            if (user.roomId && !user.isVisible) {
                const roomResult = await requestsRepository.RoomApiService.getRoom(user.roomId);
                if (!roomResult.success) {
                    toast.error(roomResult.message);
                    setSaveLoading(false);
                    return;
                }
                if (!roomResult.result?.room?.chat?.id) {
                    toast.error("Чат не найден");
                    setSaveLoading(false);
                    return;
                }
                const accessResult = await requestsRepository.ChatApiService.changeVisible(userId, roomResult.result.room.chat.id, true);
                if (!accessResult.success) {
                    toast.error(accessResult.message);
                    setSaveLoading(false);
                    return;
                }
                setSaveLoading(false);
                onSuccess(roomResult.result.room.id);
                return;
            }
            const roomResult = await requestsRepository.RoomApiService.createPrivateRoom();
            if (!roomResult.success) {
                toast.error(roomResult.message);
                setSaveLoading(false);
                return;
            }
            roomId = roomResult.result;
            
            const mainUserResult = await requestsRepository.RoomApiService.addUserToPrivateRoom(roomId,
                userId,
                userName,
                user.fullName,
                true,
                user.organizationName
                );
            if (!mainUserResult.success) {
                toast.error(mainUserResult.message);
                setSaveLoading(false);
                return;
            }

            const newUserResult = await requestsRepository.RoomApiService.addUserToPrivateRoom(roomId,
                user.id,
                user.fullName,
                userName,
                false,
                description
            );
            if (!newUserResult.success) {
                toast.error(newUserResult.message);
                setSaveLoading(false);
                return;
            }
            
        } catch {
            toast.error("Ошибка добавления контакта");
        }
        setSaveLoading(false);
        onSuccess(roomId);
    }
    
    const canAdd = (userId: any) => {
        return !chats.find((x: any) => x.users.find((u: any) => u.userId === userId));
    }

    return (
        <div className="communication-modal-container">
        <div className="communication-modal-content" style={{width: 800}}>
            <div className="communication-modal-title communication-modal-title_contacts">
                <div className="communication-modal-title__text">Поиск контакта</div>
            </div>
            <div className="communication-modal-body communication-modal-body_contacts">
                <div className="communication-modal-content">
                    <div className={"communication-modal-content__search-container"}>
                        <Input
                            value={textValue}
                            onChange={setTextValue}
                            placeholder={"Поиск"}
                            onKeyPress={(event: any) => {
                                if (event.charCode === 13 && textValue?.trim()?.length !== 0) {
                                    search();
                                }
                            }}
                            style={{minWidth: 300}}
                        />
                        <div style={{width: '100%'}}>
                            <CustomSelect placeholder={"Организация"} loadOptions={organizationsSearch}
                                          isClearable={true} options={organizationOptions} value={organization}
                                          onChange={setOrganization} cyName={"organization-select"}/>
                        </div>
                        <CustomButton
                            onClick={() => search()}
                            disabled={textValue?.trim()?.length === 0}
                            isSmall={true}
                            isDefault={true}
                        >
                            Искать
                        </CustomButton>
                    </div>
                    <div className={"communication-modal-content__search-results"}>
                        {usersSearch ? <div>
                            <Spin small={true}/>
                        </div> : users.map((user, key) => <div onClick={() => canAdd(user.id) && setNewUser(user.id)}
                                                               className={`communication-modal-content-search-item ${canAdd(user.id) ? '' : 'communication-modal-content-search-item_disabled'} ${newUser === user.id ? 'communication-modal-content-search-item_active' : ''}`}
                                                               key={key}>
                            <RadioButton value={newUser === user.id} checked={newUser === user.id} onChange={() => () => canAdd(user.id) && setNewUser(user.id)}/>
                            <div className={'communication-modal-content-search-item-main-info'}>
                                <div className={'communication-modal-content-search-item-main-info__title'}>{user.fullName}</div>
                                <div
                                    className={'communication-modal-content-search-item-main-info__description'}>{user.organizationName}</div>
                            </div>
                        </div>)}
                    </div>
                </div>
                <div className="communication-modal-footer communication-modal-footer_start">
                    <CustomButton
                        onClick={() => addNewContact()}
                        disabled={!newUser}
                        isSmall={true}
                        loading={saveLoading}
                    >
                        Добавить
                    </CustomButton>
                    <CustomButton
                        outline
                        onClick={close}
                        isSmall={true}
                    >
                        Отменить
                    </CustomButton>
                </div>
            </div>
        </div>
            <CustomButton
                onClick={close}
                isRound={true}
                outline={true}
            ><CancelIcon /></CustomButton>
        </div>
    );
};

export const AddContact = (props: IProps): ModalComponent => {
    return ({close}) => {
        return <Modal close={close} {...props} />;
    };
};
